import { isObject, isArray } from './is';
import { RecursivePartial } from './typescript';

/**
 * DEFAULT_ID_KEYS are required to dedupe same keys in an array.
 * meaning if two objects in an array share the same value for one of these keys will be MERGED rather than duplicated
 */
const DEFAULT_ID_KEYS = ['field'];

export const deepMerge = <T extends object>(...objects: RecursivePartial<T>[]): T => {
  const [first, ...rest] = objects;
  return rest.reduce(
    (prev, obj) => {
      Object.keys(obj).forEach(key => {
        const pVal = prev[key];
        const oVal = obj[key];
        if (isArray(pVal) && isArray(oVal)) {
          prev[key] = pVal;
        } else if (isObject(pVal) && isObject(oVal)) {
          prev[key] = deepMerge(pVal as object, oVal as object);
        } else {
          prev[key] = oVal;
        }
      });

      return prev;
    },
    first ? { ...first } : {},
  ) as T;
};
function stableStringify(obj) {
  if (Array.isArray(obj)) {
    return `[${obj.map(stableStringify).join(',')}]`;
  }
  if (isObject(obj)) {
    return `{${Object.keys(obj)
      .sort()
      .map(key => `"${key}":${stableStringify(obj[key])}`)
      .join(',')}}`;
  }
  return JSON.stringify(obj);
}

export function deepObjectMerge(target, source, idKeys = DEFAULT_ID_KEYS) {
  if (Array.isArray(target) && Array.isArray(source)) {
    const map = new Map();

    [...target, ...source].forEach(item => {
      const idKey = isObject(item) ? idKeys.find(k => k in item) : null;
      const key = idKey ? item[idKey] : isObject(item) ? stableStringify(item) : String(item);

      if (map.has(key) && isObject(item)) {
        map.set(key, deepObjectMerge(map.get(key), item, idKeys));
      } else {
        map.set(key, item);
      }
    });

    return Array.from(map.values());
  }

  if (isObject(target) && isObject(source)) {
    const result = {};
    const allKeys = new Set([...Object.keys(target), ...Object.keys(source)]);

    allKeys.forEach(key => {
      if (key in target && key in source) {
        result[key] = deepObjectMerge(target[key], source[key], idKeys);
      } else if (key in source) {
        result[key] = source[key];
      } else {
        result[key] = target[key];
      }
    });

    return result;
  }

  if (isObject(target) && Array.isArray(source)) {
    const result = { ...target };
    const arrayKey = Object.keys(target).find(k => Array.isArray(target[k]));
    if (arrayKey) {
      result[arrayKey] = deepObjectMerge(target[arrayKey], source, idKeys);
    }
    return result;
  }

  if (Array.isArray(target) && isObject(source)) {
    const arrayKey = Object.keys(source).find(k => Array.isArray(source[k]));
    if (arrayKey) {
      return deepObjectMerge(target, source[arrayKey], idKeys);
    }
  }

  return source;
}
export const deepArrayMerge = <T extends object>(...objects: RecursivePartial<T>[]): T => {
  const [first, ...rest] = objects;
  return rest.reduce(
    (prev, obj) => {
      Object.keys(obj).forEach(key => {
        const pVal = prev[key];
        const oVal = obj[key];

        if (isArray(pVal) && isArray(oVal)) {
          prev[key] = pVal.concat(...oVal);
        } else if (isObject(pVal) && isObject(oVal)) {
          prev[key] = deepArrayMerge(pVal as object, oVal as object);
        } else {
          prev[key] = oVal;
        }
      });

      return prev;
    },
    first ? { ...first } : {},
  ) as T;
};
